// AGC: Machines Service
// AGC: Handles all API calls and offline queue for machine instances
// AGC: Integrates with Firebase and offline-first architecture

import { getBackendUrl } from '../utils/backendUrl'
import type { MachineInstance, MachineUsageLog } from '../types/machineInstance'
import type { MachineWorkLog } from '../types/machineWorkLog'
import { queueTask, type OfflineTaskItem } from './offlineQueue'

const BASE_URL = getBackendUrl()

/**
 * AGC: Offline machine item for queue
 */
export interface OfflineMachineItem extends OfflineTaskItem {
  type: 'create' | 'update' | 'delete'
  taskData: Partial<MachineInstance>
}

/**
 * AGC: Upload machine photo to backend
 * AGC: Backend handles Firebase Storage upload to avoid CORS issues
 */
export const uploadMachinePhoto = async (
  photoFile: File,
  authToken?: string | null
): Promise<string> => {
  try {
    // AGC: Validate file type
    if (!photoFile.type.startsWith('image/')) {
      throw new Error('Only image files are allowed')
    }

    // AGC: Validate file size (10MB max)
    if (photoFile.size > 10 * 1024 * 1024) {
      throw new Error('File size exceeds 10MB limit')
    }

    // AGC: Create FormData
    const formData = new FormData()
    formData.append('photo', photoFile)

    const headers: HeadersInit = {}

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/upload-photo`, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Failed to upload photo: ${response.statusText}`)
    }

    const data = await response.json()
    return data.url
  } catch (error) {
    console.error('AGC: Error uploading machine photo:', error)
    throw error
  }
}

/**
 * AGC: Get all machines for the authenticated user
 */
export const getMachines = async (authToken?: string | null): Promise<MachineInstance[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.')
      }
      throw new Error(`Failed to fetch machines: ${response.statusText}`)
    }

    const data = await response.json()
    return data.machines || []
  } catch (error) {
    console.error('AGC: Error fetching machines:', error)
    return []
  }
}

/**
 * AGC: Get a single machine by ID
 */
export const getMachine = async (
  machineId: string,
  authToken?: string | null
): Promise<MachineInstance | null> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch machine: ${response.statusText}`)
    }

    const data = await response.json()
    return data.machine
  } catch (error) {
    console.error('AGC: Error fetching machine:', error)
    return null
  }
}

/**
 * AGC: Create a new machine
 * AGC: Works offline - queues if no connection
 */
export const createMachine = async (
  machineData: Partial<MachineInstance>,
  authToken?: string | null,
  offline: boolean = false
): Promise<MachineInstance> => {
  // AGC: If offline or explicitly requested, queue for later
  if (offline || !navigator.onLine) {
    const offlineItem: OfflineMachineItem = {
      id: `machine-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'create',
      taskData: machineData,
      userId: authToken || 'unknown',
      createdAt: new Date().toISOString(),
      retryCount: 0,
    }

    await queueTask(offlineItem)
    console.log('🔄 AGC: Machine queued for creation:', offlineItem.id)

    // AGC: Return temporary machine object for optimistic UI
    return {
      ...machineData,
      id: offlineItem.id,
      createdAt: offlineItem.createdAt,
      updatedAt: offlineItem.createdAt,
      horimeterHours: machineData.horimeterHours || 0,
      hectaresWorked: machineData.hectaresWorked || 0,
      status: machineData.status || 'available',
      isArchived: false,
    } as MachineInstance
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines`, {
      method: 'POST',
      headers,
      body: JSON.stringify(machineData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create machine: ${response.statusText}`)
    }

    const data = await response.json()
    return data.machine
  } catch (error) {
    console.error('AGC: Error creating machine:', error)
    throw error
  }
}

/**
 * AGC: Update an existing machine
 * AGC: Works offline - queues if no connection
 */
export const updateMachine = async (
  machineId: string,
  machineData: Partial<MachineInstance>,
  authToken?: string | null,
  offline: boolean = false
): Promise<MachineInstance> => {
  // AGC: If offline or explicitly requested, queue for later
  if (offline || !navigator.onLine) {
    const offlineItem: OfflineMachineItem = {
      id: `machine-update-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'update',
      taskData: { ...machineData, id: machineId },
      userId: authToken || 'unknown',
      createdAt: new Date().toISOString(),
      retryCount: 0,
    }

    await queueTask(offlineItem)
    console.log('🔄 AGC: Machine update queued:', machineId)

    // AGC: Return optimistic update
    return {
      ...machineData,
      id: machineId,
      updatedAt: new Date().toISOString(),
    } as MachineInstance
  }

  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(machineData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to update machine: ${response.statusText}`)
    }

    const data = await response.json()
    return data.machine
  } catch (error) {
    console.error('AGC: Error updating machine:', error)
    throw error
  }
}

/**
 * AGC: Delete (archive) a machine
 */
export const deleteMachine = async (
  machineId: string,
  authToken?: string | null
): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}`, {
      method: 'DELETE',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to delete machine: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error deleting machine:', error)
    throw error
  }
}

/**
 * AGC: Get usage logs for a machine
 */
export const getMachineUsageLogs = async (
  machineId: string,
  authToken?: string | null
): Promise<MachineUsageLog[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/logs`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch usage logs: ${response.statusText}`)
    }

    const data = await response.json()
    return data.logs || []
  } catch (error) {
    console.error('AGC: Error fetching usage logs:', error)
    return []
  }
}

/**
 * AGC: Get work logs for a machine
 * AGC: Fetches from machineWorkLogs collection
 */
export const getMachineWorkLogs = async (
  machineId: string,
  authToken?: string | null
): Promise<MachineWorkLog[]> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machine-work-logs?machineId=${machineId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch work logs: ${response.statusText}`)
    }

    const data = await response.json()
    return data || []
  } catch (error) {
    console.error('AGC: Error fetching work logs:', error)
    return []
  }
}

/**
 * AGC: Create a usage log for a machine
 * AGC: Called when a task is completed
 */
export const createMachineUsageLog = async (
  logData: Partial<MachineUsageLog>,
  authToken?: string | null
): Promise<MachineUsageLog> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machine-logs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(logData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create usage log: ${response.statusText}`)
    }

    const data = await response.json()
    return data.log
  } catch (error) {
    console.error('AGC: Error creating usage log:', error)
    throw error
  }
}

/**
 * AGC: Increment machine hours and hectares
 * AGC: Called when task is completed
 */
export const incrementMachineUsage = async (
  machineId: string,
  hoursUsed: number,
  hectaresWorked?: number,
  authToken?: string | null
): Promise<void> => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`
    }

    const response = await fetch(`${BASE_URL}/api/v1/machines/${machineId}/increment`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ hoursUsed, hectaresWorked }),
    })

    if (!response.ok) {
      throw new Error(`Failed to increment machine usage: ${response.statusText}`)
    }
  } catch (error) {
    console.error('AGC: Error incrementing machine usage:', error)
    throw error
  }
}

/**
 * AGC: Get machines available for assignment (not in use)
 */
export const getAvailableMachines = async (
  authToken?: string | null
): Promise<MachineInstance[]> => {
  const machines = await getMachines(authToken)
  return machines.filter(
    (m) => !m.isArchived && (m.status === 'available' || m.status === 'in_use')
  )
}
